import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReceiptIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, InlineAlert, StatusPill } from '@/components/ui';
import { useSelection, useTableActions, useTablesData } from '@/context/TablesContext';
// The one-page BillModal is parked (see BillModal.tsx); BillStepsModal takes the same props.
import { clearBookingBillDrafts } from '@/features/billing/billDraft';
import { BillStepsModal } from '@/features/billing/components/BillStepsModal';
import { TableGrid } from '@/features/tables/components/TableGrid';
import { formatClock, formatLongDate } from '@/utils/format';
import styles from '@/styles/pages/TablesPage/style.scss';

/**
 * The date and time under the title. Its own component, ticking on each minute boundary, so the clock re-renders
 * only itself, never the page, the cards or an open bill.
 */
function HeaderClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(() => {
        setNow(new Date());
        schedule();
      }, 60_000 - (Date.now() % 60_000) + 50);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);
  return (
    <>
      {formatLongDate(now)} · <span className={styles.clock}>{formatClock(now)}</span>
    </>
  );
}

export function TablesPage() {
  const { tables, loading, error } = useTablesData();
  const { selection, selectedBookings } = useSelection();
  const { dismissError, clearAllSelections } = useTableActions();
  const [billing, setBilling] = useState(false);

  const openBill = useCallback(() => setBilling(true), []);
  const closeBill = useCallback(() => setBilling(false), []);

  // A bill closed half-way resumes while its bookings stay ticked; with everything unticked it is over.
  const nothingTicked = selectedBookings.length === 0;
  useEffect(() => {
    if (nothingTicked) clearBookingBillDrafts();
  }, [nothingTicked]);

  // Tables with ticked bookings. One table: its card offers Generate Bill. Several: the header does.
  const selectedTableIds = useMemo(() => new Set(selectedBookings.map((b) => b.tableId)), [selectedBookings]);
  const multiTable = selectedTableIds.size > 1;
  const count = (s: string) => tables.filter((t) => t.status === s).length;

  return (
    <>
      <PageHeader
        title="Tables"
        subtitle={<HeaderClock />}
        aside={
          multiTable ? (
            <div className={styles.multiBill}>
              <span className={styles.selectedCount}>
                {selectedBookings.length} selected · {selectedTableIds.size} tables
              </span>
              <Button variant="ghost" size="md" onClick={clearAllSelections} className={styles.clear}>
                Clear all
              </Button>
              <Button
                variant="primary"
                size="md"
                icon={<ReceiptIcon size={16} />}
                aria-label={`Generate one bill for ${selectedBookings.length} bookings across tables`}
                onClick={openBill}
              >
                Generate Bill
              </Button>
            </div>
          ) : (
            <>
              <StatusPill tone="green" label={`${count('playing')} playing`} />
              <StatusPill tone="amber" label={`${count('paused')} paused`} />
              <StatusPill tone="blue" hollow label={`${count('available')} available`} />
            </>
          )
        }
      />

      {error && <InlineAlert message={error} onDismiss={dismissError} />}

      {loading ? (
        <p className={styles.muted}>Loading tables…</p>
      ) : tables.length === 0 ? (
        <p className={styles.muted}>No tables yet. Run `npm run seed` to add the club's tables.</p>
      ) : (
        <TableGrid tables={tables} selection={selection} multiTable={multiTable} onGenerateBill={openBill} />
      )}

      {billing && <BillStepsModal bookings={selectedBookings} onClose={closeBill} />}
    </>
  );
}
